import React, { useLayoutEffect, useMemo, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { format } from 'date-fns';
import Icon from '../../../components/AppIcon';
import Button from '../../../components/ui/Button';
import CallSummaryModal from './CallSummaryModal';
import CallStatusBadge from '../../ai-assistant/components/CallStatusBadge';
import { cn } from '../../../utils/cn';
import { getCandidateProfilePath } from '../../../lib/utils/candidateRoutes';

const ENGLISH_PLACEHOLDERS = new Set([
  'No AI calls recorded yet.',
  'No summary available.',
]);

const SummaryCard = ({ text, header, onExpand, expandPayload, className, isExpandable }) => {
  const { t } = useTranslation();
  const clampRef = useRef(null);
  const measureRef = useRef(null);
  const [isTruncated, setIsTruncated] = useState(false);
  const canExpand = Boolean(isExpandable) && isTruncated;

  useLayoutEffect(() => {
    const clamped = clampRef.current;
    const measure = measureRef.current;
    if (!clamped || !measure || !isExpandable) {
      setIsTruncated(false);
      return undefined;
    }

    const updateTruncation = () => {
      setIsTruncated(measure.offsetHeight > clamped.offsetHeight + 1);
    };

    updateTruncation();

    const frameId = window.requestAnimationFrame(updateTruncation);
    const observer = new ResizeObserver(updateTruncation);
    observer.observe(clamped);
    observer.observe(measure);

    return () => {
      window.cancelAnimationFrame(frameId);
      observer.disconnect();
    };
  }, [text, isExpandable]);

  const handleOpen = () => {
    if (canExpand) onExpand(expandPayload);
  };

  const handleKeyDown = (event) => {
    if (!canExpand) return;
    if (event.key === 'Enter' || event.key === ' ') {
      event.preventDefault();
      handleOpen();
    }
  };

  return (
    <div
      className={cn(
        'bg-muted/50 rounded-lg p-3 md:p-4 border border-border',
        canExpand && 'cursor-pointer transition-colors hover:bg-muted',
        className
      )}
      onClick={canExpand ? handleOpen : undefined}
      onKeyDown={handleKeyDown}
      role={canExpand ? 'button' : undefined}
      tabIndex={canExpand ? 0 : undefined}
    >
      {header}

      <div className="relative">
        <p
          ref={measureRef}
          aria-hidden="true"
          className="pointer-events-none absolute inset-x-0 top-0 invisible h-auto w-full text-xs md:text-sm leading-relaxed"
        >
          {text}
        </p>
        <p
          ref={clampRef}
          className="text-xs md:text-sm text-foreground leading-relaxed line-clamp-3"
        >
          {text}
        </p>
      </div>

      {canExpand && (
        <span className="inline-block text-xs md:text-sm text-primary font-medium mt-1">
          {t('common.readMore')}
        </span>
      )}
    </div>
  );
};

const HISTORY_PREVIEW_LIMIT = 2;

const AICallSummaryCard = ({ candidate, hideViewAll = false }) => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const hasCalls = Boolean(candidate?.lastCallAt);
  const history = candidate?.callHistory ?? [];
  const visibleHistory = history.slice(0, HISTORY_PREVIEW_LIMIT);
  const showViewAll = history.length > HISTORY_PREVIEW_LIMIT;
  const [expandedSummary, setExpandedSummary] = useState(null);

  const formatCallDate = (date) => (
    date ? format(new Date(date), 'dd-MM-yyyy') : t('common.na')
  );

  const resolveSummary = useMemo(() => {
    const placeholders = new Set([
      ...ENGLISH_PLACEHOLDERS,
      t('candidateProfile.noCallsYet'),
      t('candidateProfile.noSummary'),
    ]);
    return (raw, emptyKey) => {
      const trimmed = raw?.trim?.() || '';
      if (!trimmed || placeholders.has(trimmed)) {
        return { text: t(emptyKey), expandable: false };
      }
      return { text: trimmed, expandable: true };
    };
  }, [t]);

  const lastCallSummary = resolveSummary(
    candidate?.callSummary,
    hasCalls ? 'candidateProfile.noSummary' : 'candidateProfile.noCallsYet',
  );

  const openSummary = (summary) => setExpandedSummary(summary);
  const closeSummary = () => setExpandedSummary(null);

  const handleViewAll = () => {
    if (!candidate?.id) return;
    navigate(`/ai-assistant/candidate/${candidate.id}`, {
      state: { returnTo: getCandidateProfilePath(candidate.id) },
    });
  };

  return (
    <>
      <div className="bg-card rounded-lg border border-border p-4 md:p-6 lg:p-8 shadow-elevation-sm">
        <div className="flex items-center gap-2 mb-4 md:mb-6">
          <Icon name="Phone" size={22} className="text-accent" />
          <h2 className="text-xl md:text-2xl font-semibold text-foreground">
            {t('candidateProfile.aiCallSummary')}
          </h2>
        </div>

        <h3 className="text-base md:text-lg font-semibold text-foreground mb-3 md:mb-4">
          {t('candidateProfile.lastCall')}
        </h3>

        <SummaryCard
          text={lastCallSummary.text}
          isExpandable={lastCallSummary.expandable}
          header={(
            <div className="flex flex-wrap items-center gap-2 mb-1.5">
              <p className="text-sm md:text-base text-muted-foreground">
                {formatCallDate(candidate?.lastCallAt)}
              </p>
              {hasCalls && candidate?.lastCallStatus ? (
                <CallStatusBadge status={candidate.lastCallStatus} />
              ) : null}
            </div>
          )}
          onExpand={openSummary}
          expandPayload={{
            title: t('candidateProfile.lastCall'),
            date: formatCallDate(candidate?.lastCallAt),
            status: candidate?.lastCallStatus,
            text: lastCallSummary.text,
          }}
        />

        {hasCalls && visibleHistory.length > 0 && (
          <div className="mt-6 md:mt-8">
            <div className="flex items-center justify-between gap-3 mb-3 md:mb-4">
              <h3 className="text-base md:text-lg font-semibold text-foreground">
                {t('candidateProfile.history')}
              </h3>
              {showViewAll && !hideViewAll && (
                <Button
                  variant="outline"
                  size="sm"
                  onClick={handleViewAll}
                  iconName="ArrowRight"
                  iconPosition="right"
                >
                  {t('common.viewAll')}
                </Button>
              )}
            </div>
            <div className="space-y-2">
              {visibleHistory.map((call) => {
                const summary = resolveSummary(call.ai_summary, 'candidateProfile.noSummary');
                return (
                  <SummaryCard
                    key={call.id}
                    text={summary.text}
                    isExpandable={summary.expandable}
                    header={(
                      <p className="text-sm md:text-base text-muted-foreground mb-1.5">
                        {formatCallDate(call.created_at)}
                      </p>
                    )}
                    onExpand={openSummary}
                    expandPayload={{
                      title: t('candidateProfile.callSummary'),
                      date: formatCallDate(call.created_at),
                      status: call.status,
                      text: summary.text,
                    }}
                  />
                );
              })}
            </div>
          </div>
        )}
      </div>

      {createPortal(
        <CallSummaryModal
          isOpen={Boolean(expandedSummary)}
          onClose={closeSummary}
          title={expandedSummary?.title}
          date={expandedSummary?.date}
          status={expandedSummary?.status}
          text={expandedSummary?.text}
        />,
        document.body
      )}
    </>
  );
};

export default AICallSummaryCard;
